import React, { useState, useEffect } from 'react';
import axios from 'axios';

function FeedbackForm({ user }) {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!feedback.trim()) {
      showMessage('Please enter your feedback', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/feedback', {
        feedback: feedback.trim(),
        email: email.trim() || null,
        context: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });

      showMessage('Thank you for your interest! We\'ll be in touch within 1-2 business days.', 'success');
      setFeedback('');
      setEmail('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showMessage('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 5000);
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header - Hidden on mobile */}
      <header className="hidden lg:flex items-center justify-between px-12 py-8 border-b border-gray-100 bg-white">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-[36px]">rocket_launch</span>
            Build Your Custom AI Solution
          </h1>
          <p className="text-gray-500 mt-1">This demo is just the beginning. Tell us about your specific needs and let's create something powerful together.</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto px-4 py-6 lg:px-12 lg:py-10 bg-gray-50/30">
        <div className="max-w-3xl mx-auto">
          {/* Success/Error Banner */}
          {message && (
            <div
              className={`mb-6 px-6 py-4 rounded-input border flex items-center gap-3 ${
                message.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              <span className="material-symbols-outlined">
                {message.type === 'success' ? 'check_circle' : 'error'}
              </span>
              <span className="font-medium">{message.text}</span>
            </div>
          )}

          {/* Feedback Form Card */}
          <div className="bg-white border border-gray-200 rounded-input shadow-sm p-5 lg:p-8">
            {/* Value Proposition */}
            <div className="mb-8 pb-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Ready to Transform Your Business?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You've experienced what AI can do with this demo. Now imagine a solution custom-built for your unique challenges—whether it's customer support automation, internal knowledge management, process optimization, or something entirely new.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">check_circle</span>
                  <span className="text-sm text-gray-700 font-medium">Custom-trained on your data</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">check_circle</span>
                  <span className="text-sm text-gray-700 font-medium">Tailored to your workflow</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">check_circle</span>
                  <span className="text-sm text-gray-700 font-medium">Full implementation support</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Use Case Textarea */}
              <div>
                <label htmlFor="feedback" className="block text-sm font-semibold text-gray-900 mb-2">
                  What's Your Use Case? <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Describe your specific needs, challenges, or use case scenarios...&#10;&#10;Examples:&#10;• Automate customer support for our e-commerce platform&#10;• Build an internal knowledge base for our 500+ employee team&#10;• Create a personalized product recommendation agent&#10;• Develop a compliance document Q&A system for our legal team&#10;&#10;Tell us about your industry, current pain points, and what success looks like for you."
                  rows="10"
                  required
                  disabled={submitting}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-input text-[15px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-2">
                  The more details you share, the better we can understand how to help you succeed.
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Business Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-[20px]">
                    business
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.name@company.com"
                    required
                    disabled={submitting}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-input text-[15px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  We'll reach out to schedule a consultation and discuss your custom solution.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !feedback.trim() || !email.trim()}
                className="w-full px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-input font-semibold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform active:scale-95"
              >
                {submitting ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">rocket_launch</span>
                    <span>Request Consultation</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info Card */}
          <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-input p-6">
            <div className="flex gap-3">
              <span className="material-symbols-outlined text-primary text-[24px]">handshake</span>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What Happens Next?</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Our team will review your use case and reach out within <strong>1-2 business days</strong> to:
                </p>
                <ul className="text-sm text-gray-600 space-y-2 ml-1">
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-[18px] mt-0.5">arrow_forward</span>
                    <span>Schedule a free consultation to dive deeper into your needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-[18px] mt-0.5">arrow_forward</span>
                    <span>Provide a preliminary solution design and timeline estimate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-[18px] mt-0.5">arrow_forward</span>
                    <span>Discuss pricing, implementation, and ongoing support options</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 leading-relaxed mt-4 pt-4 border-t border-blue-200">
                  <strong>No commitment required.</strong> Let's explore how AI can solve your specific challenges.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default FeedbackForm;
